import React, { useState, useEffect } from 'react';
import { Building2, Loader2, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SSOLoginButton = ({ onLoginSuccess, onLoginError, variant = "outline", className = "" }) => {
  const [loading, setLoading] = useState(false);
  const [ssoConfig, setSsoConfig] = useState(null);
  const [error, setError] = useState(null);
  const [showPopupBlockedDialog, setShowPopupBlockedDialog] = useState(false);

  useEffect(() => {
    fetchSSOConfig();
  }, []);

  const fetchSSOConfig = async () => {
    try {
      const response = await axios.get(`${API}/auth/v2/sso/config`);
      setSsoConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch SSO config:', err);
    }
  };

  const handleSSOLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get auth URL from backend
      const response = await axios.get(`${API}/auth/v2/sso/login`);
      const { auth_url, state } = response.data;

      // Store state for validation
      sessionStorage.setItem('sso_state', state);

      // Calculate popup dimensions
      const width = 500;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // Open Microsoft login in popup
      const popup = window.open(
        auth_url,
        'Microsoft SSO Login',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );

      if (!popup || popup.closed) {
        setShowPopupBlockedDialog(true);
        setLoading(false);
        return;
      }

      // Listen for callback
      const handleMessage = async (event) => {
        // Verify origin
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'SSO_CALLBACK') {
          window.removeEventListener('message', handleMessage);
          popup.close();

          const { code, state: returnedState, error: authError } = event.data;

          if (authError) {
            setError(authError);
            setLoading(false);
            onLoginError?.(authError);
            return;
          }

          // Validate state
          const storedState = sessionStorage.getItem('sso_state');
          if (returnedState !== storedState) {
            setError('Security validation failed. Please try again.');
            setLoading(false);
            return;
          }

          // Exchange code for token
          try {
            const tokenResponse = await axios.post(`${API}/auth/v2/sso/callback`, {
              code,
              state: returnedState
            });

            const { session_token, user } = tokenResponse.data;

            // Store session
            localStorage.setItem('session_token', session_token);
            localStorage.setItem('user', JSON.stringify(user));

            // Configure axios default header
            axios.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;

            setLoading(false);
            onLoginSuccess?.(user);
          } catch (tokenError) {
            const errorMsg = tokenError.response?.data?.detail || 'SSO login failed';
            setError(errorMsg);
            setLoading(false);
            onLoginError?.(errorMsg);
          }
        }
      };

      window.addEventListener('message', handleMessage);

      // Poll for popup close (fallback if postMessage fails)
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', handleMessage);
          setLoading(false);
        }
      }, 500);

      // Cleanup after 5 minutes
      setTimeout(() => {
        clearInterval(pollTimer);
        window.removeEventListener('message', handleMessage);
        if (!popup.closed) popup.close();
        setLoading(false);
      }, 300000);

    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to initiate SSO login';
      setError(errorMsg);
      setLoading(false);
      onLoginError?.(errorMsg);
    }
  };

  // Don't render if SSO is not enabled
  if (!ssoConfig?.enabled) {
    return null;
  }

  return (
    <>
      <Button
        variant={variant}
        className={`w-full ${className}`}
        onClick={handleSSOLogin}
        disabled={loading}
        data-testid="sso-login-btn"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Building2 className="h-4 w-4 mr-2" />
        )}
        {loading ? 'Signing in...' : `Sign in with ${ssoConfig.tenant_name || 'Microsoft 365'}`}
      </Button>

      {error && (
        <Alert variant="destructive" className="mt-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Dialog open={showPopupBlockedDialog} onOpenChange={setShowPopupBlockedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pop-up Blocked</DialogTitle>
            <DialogDescription>
              Please allow pop-ups for this site to use Microsoft SSO login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              To enable pop-ups:
            </p>
            <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
              <li>Click the blocked pop-up icon in your browser's address bar</li>
              <li>Select "Always allow pop-ups from this site"</li>
              <li>Click the SSO login button again</li>
            </ol>
            <Button onClick={() => setShowPopupBlockedDialog(false)} className="w-full">
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Callback page component - place at /auth/sso/callback route
export const SSOCallbackPage = () => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = () => {
      // Parse URL parameters
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const authError = params.get('error');
      const errorDescription = params.get('error_description');

      if (authError) {
        setStatus('error');
        setError(errorDescription || authError);
        
        // Notify parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'SSO_CALLBACK',
            error: errorDescription || authError
          }, window.location.origin);
        }
        return;
      }

      if (code) {
        setStatus('success');
        
        // Notify parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'SSO_CALLBACK',
            code,
            state
          }, window.location.origin);
        } else {
          // If not in popup, redirect with code (for redirect flow)
          // This handles the case where popup was blocked and we did full redirect
          handleDirectCallback(code, state);
        }
      } else {
        setStatus('error');
        setError('No authorization code received');
      }
    };

    const handleDirectCallback = async (code, state) => {
      try {
        const response = await axios.post(`${API}/auth/v2/sso/callback`, {
          code,
          state
        });

        const { session_token, user } = response.data;
        localStorage.setItem('session_token', session_token);
        localStorage.setItem('user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;
        
        window.location.href = '/dashboard';
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'SSO login failed');
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4 p-8">
        {status === 'processing' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="text-lg">Processing SSO login...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
            <p className="text-lg">Login successful!</p>
            <p className="text-sm text-muted-foreground">This window will close automatically.</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
            <p className="text-lg">Login Failed</p>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button onClick={() => window.close()} variant="outline">
              Close Window
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SSOLoginButton;
